import { roundRect2 } from '../../renderers';
import { lineFont } from '../../state/font';
import { BasicObject } from '../../uiObjects/basicObject';
import { getBasicButton } from '../../uiObjects/button';

export type GridUndoRedoDirection = 'left' | 'right';

export interface GridHistoryButtonState {
  hasHistory?: boolean;
}

interface GridUndoRedoButtonOptions {
  $canvas: JQuery<HTMLElement>;
  direction: GridUndoRedoDirection;
  dimension: number;
  getThemeColor: () => string;
  isSelected: () => boolean;
  isCorrect: () => boolean;
  isDisabled?: () => boolean;
  canUseHistory: () => boolean;
  consumeDisabledClick?: () => boolean;
  onGrabFocus: () => void;
  onClick: () => boolean;
}

export interface GridUndoRedoButton
  extends BasicObject,
    Required<GridHistoryButtonState> {}

const getButtonLabel = (direction: GridUndoRedoDirection): string =>
  direction === 'left' ? 'Undo' : 'Redo';

const isButtonDisabled = (isDisabled?: () => boolean): boolean =>
  Boolean(isDisabled?.());

const isButtonClickable = (
  isCorrect: () => boolean,
  canUseHistory: () => boolean,
  isDisabled?: () => boolean
): boolean => !isCorrect() && !isButtonDisabled(isDisabled) && canUseHistory();

const isButtonHighlighted = (
  button: GridUndoRedoButton,
  isSelected: () => boolean,
  isCorrect: () => boolean,
  isDisabled?: () => boolean
): boolean =>
  button.hasHistory &&
  isSelected() &&
  !isCorrect() &&
  !isButtonDisabled(isDisabled);

const drawCurvedUndoRedoArrow = (
  ctx: CanvasRenderingContext2D,
  direction: GridUndoRedoDirection,
  dimension: number,
  color: string
): void => {
  // These arcs mirror the legacy canvas geometry so the extracted button
  // paints the exact same icon shape as the inline implementation.
  const centerX = dimension / 2;
  const centerY = dimension / 2 - 6;
  const radius = 8;

  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  ctx.translate(0, 4);

  if (direction === 'left') {
    const leftStart = -0.05 * Math.PI;
    const leftEnd = -0.72 * Math.PI;

    ctx.beginPath();
    ctx.arc(centerX + 1, centerY, radius, leftStart, leftEnd, true);
    ctx.stroke();

    const arrowBaseX = centerX + 1 + radius * Math.cos(leftEnd);
    const arrowBaseY = centerY + radius * Math.sin(leftEnd);

    ctx.beginPath();
    ctx.moveTo(arrowBaseX - 3, arrowBaseY - 3);
    ctx.lineTo(arrowBaseX + 1, arrowBaseY + 3);
    ctx.lineTo(arrowBaseX - 7, arrowBaseY + 6);
    ctx.closePath();
    ctx.fill();

    ctx.restore();

    return;
  }

  const rightStart = -0.95 * Math.PI;
  const rightEnd = -0.28 * Math.PI;

  ctx.beginPath();
  ctx.arc(centerX - 1, centerY, radius, rightStart, rightEnd, false);
  ctx.stroke();

  const arrowBaseX = centerX - 1 + radius * Math.cos(rightEnd);
  const arrowBaseY = centerY + radius * Math.sin(rightEnd);

  ctx.beginPath();
  ctx.moveTo(arrowBaseX + 3, arrowBaseY - 3);
  ctx.lineTo(arrowBaseX - 1, arrowBaseY + 3);
  ctx.lineTo(arrowBaseX + 7, arrowBaseY + 6);
  ctx.closePath();
  ctx.fill();

  ctx.restore();
};

export function getGridUndoRedoButton(
  options: GridUndoRedoButtonOptions
): GridUndoRedoButton {
  const {
    $canvas,
    direction,
    dimension,
    getThemeColor,
    isSelected,
    isCorrect,
    isDisabled,
    canUseHistory,
    consumeDisabledClick,
    onGrabFocus,
    onClick,
  } = options;

  const button = getBasicButton($canvas) as GridUndoRedoButton;
  const label = getButtonLabel(direction);

  button.gmmName = `grid ${label.toLowerCase()} button`;
  button.hasHistory = false;
  button.setAllDim(dimension, dimension);

  button.mouseMoveResponse = function () {
    if (
      button.mouseIsOver &&
      isButtonClickable(isCorrect, canUseHistory, isDisabled)
    ) {
      $canvas.css('cursor', 'pointer');

      return;
    }

    $canvas.css('cursor', 'default');
  };

  button.paintMe = function (ctx) {
    ctx.save();

    const enabled = isButtonHighlighted(
      button,
      isSelected,
      isCorrect,
      isDisabled
    );

    if (enabled || button.animated) {
      roundRect2(
        ctx,
        0,
        0,
        dimension,
        dimension,
        8,
        button.animated ? getThemeColor() : 'lightgray',
        undefined,
        undefined,
        true
      );
    }

    const iconColor = button.animated
      ? '#ffffff'
      : enabled
      ? '#333333'
      : '#bcbcbc';

    drawCurvedUndoRedoArrow(ctx, direction, dimension, iconColor);

    ctx.fillStyle = iconColor;
    ctx.font = 'bold 12px ' + lineFont;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, button.viewportW / 2, button.viewportH / 2 + 8);

    ctx.restore();
  };

  button.mouseDownResponse = function () {
    const clickable = isButtonClickable(isCorrect, canUseHistory, isDisabled);

    // BasicObject will animate after mouseDownResponse returns, so this needs
    // to track the current clickable state.
    button.animates = clickable;

    if (!clickable) {
      return consumeDisabledClick ? consumeDisabledClick() : false;
    }

    onGrabFocus();

    return onClick();
  };

  return button;
}
